import { useCallback } from "react";
import { useMutation } from "@apollo/client";
import { getOperationName } from "utils/apollo";
import { CREATE_EQUIPMENT, DELETE_EQUIPMENT, UPDATE_EQUIPMENT, GET_LIST_OF_EQUIPMENT_BY_VO_ID } from "../api/equipment";
import {
  CreateUpdateEquipmentResponse,
  CreateEquipmentVariables,
  UpdateEquipmentVariables,
  DeleteEquipmentVariables,
} from "../api/equipment/types";

export function useEquipmentActions() {
  const [createEquipment, { loading: isEquipmentCreating }] = useMutation<
    CreateUpdateEquipmentResponse,
    CreateEquipmentVariables
  >(CREATE_EQUIPMENT, {
    refetchQueries: [getOperationName(GET_LIST_OF_EQUIPMENT_BY_VO_ID)],
  });

  const [updateEquipment, { loading: isEquipmentUpdating }] = useMutation<
    CreateUpdateEquipmentResponse,
    UpdateEquipmentVariables
  >(UPDATE_EQUIPMENT, {
    refetchQueries: [getOperationName(GET_LIST_OF_EQUIPMENT_BY_VO_ID)],
  });

  const [deleteEquipment, { loading: isEquipmentDeleting }] = useMutation<void, DeleteEquipmentVariables>(
    DELETE_EQUIPMENT,
    {
      refetchQueries: [getOperationName(GET_LIST_OF_EQUIPMENT_BY_VO_ID)],
    }
  );

  const onCreateEquipment = useCallback(
    async (variables: CreateEquipmentVariables["input"]) => {
      const { data } = await createEquipment({ variables: { input: variables } });

      if (!data?.upsertEquipment?.id) throw new Error("Equipment not created");

      return data.upsertEquipment.id;
    },
    [createEquipment]
  );

  const onUpdateEquipment = useCallback(
    async (variables: UpdateEquipmentVariables["input"]) => {
      const { data } = await updateEquipment({ variables: { input: variables } });

      if (!data?.upsertEquipment?.id) throw new Error("Equipment not updated");

      return data.upsertEquipment.id;
    },
    [updateEquipment]
  );

  const onDeleteEquipment = useCallback(
    async (variables: DeleteEquipmentVariables) => {
      await deleteEquipment({ variables });
    },
    [deleteEquipment]
  );

  return {
    onCreateEquipment,
    isEquipmentCreating,
    onUpdateEquipment,
    isEquipmentUpdating,
    onDeleteEquipment,
    isEquipmentDeleting,
    isAnyActionInProgress: isEquipmentDeleting || isEquipmentUpdating || isEquipmentCreating,
  };
}
